import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextField } from '@app/components/form-fields/TextField';
import useChannelPartners from '@app/hooks/channelPartners/useChannelPartners';
import type { Brand } from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ModalProps } from 'antd';
import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';
import { countryOptions } from '@app/utils/constants';

const schema = z.object({
    address_line1: z.string().trim().min(1, 'Address line 1 is required'),
    address_line2: z.string().trim(),
    channel_id: z.string('Channel Partner ID is required'),
    city: z.string().trim().min(1, 'City is required'),
    contact_email: z
        .string()
        .email('Admin email must be valid format')
        .trim()
        .min(1, 'Admin email is required'),
    contact_name: z.string().trim().min(1, 'Name is required'),
    contact_telephone: z.string().trim().min(1, 'Contact telephone is required'),
    country: z.string().trim().min(1, 'Country is required'),
    name: z.string().trim().min(1, 'Name is required'),
    post_code: z.string().trim().min(1, 'Post code is required'),
});

export type BrandFormValues = z.infer<typeof schema>;

export type BrandFormProps = ModalProps & {
    id: string;
    data?: Brand;
    isEdit?: boolean;
    onSubmit: (data: BrandFormValues, reset: UseFormReset<BrandFormValues>) => void;
};

export const BrandForm = ({
    onCancel,
    onSubmit,
    data,
    isEdit = false,
    ...props
}: BrandFormProps) => {
    const { isLoading, data: channelPartnersData } = useChannelPartners({
        page: 1,
        per_page: 100,
    });
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm<BrandFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            address_line1: '',
            address_line2: '',
            channel_id: undefined,
            city: '',
            contact_email: '',
            contact_name: '',
            contact_telephone: '',
            country: 'GB',
            name: '',
            post_code: '',
        },
    });

    useEffect(() => {
        reset({
            address_line1: data?.address_line1 ?? '',
            address_line2: data?.address_line2 ?? '',
            channel_id: data?.channel_id ?? undefined,
            city: data?.city ?? '',
            contact_email: data?.contact_email ?? '',
            contact_name: data?.contact_name ?? '',
            contact_telephone: data?.contact_telephone ?? '',
            country: data?.country ?? 'GB',
            name: data?.name ?? '',
            post_code: data?.post_code ?? '',
        });
    }, [data, reset]);

    return (
        <form onSubmit={handleSubmit((values) => onSubmit(values, reset))} id={props.id}>
            <div className="form-fields-wrapper">
                <div className="grid">
                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Channel Brand"
                                    type="text"
                                    placeholder="Enter channel brand"
                                />
                            )}
                        />
                    </div>
                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="channel_id"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Channel Partner"
                                    placeholder="Select channel partner"
                                    options={
                                        channelPartnersData?.data?.map((cp) => ({
                                            label: cp.name,
                                            value: cp.id.toString(),
                                        })) || []
                                    }
                                    loading={isLoading}
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="contact_name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Admin Name"
                                    type="text"
                                    placeholder="Enter admin name"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="contact_email"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    disabled={isEdit}
                                    label="Admin Email"
                                    type="email"
                                    placeholder="Enter admin email"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="contact_telephone"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Admin Phone"
                                    type="tel"
                                    placeholder="Enter admin phone"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="address_line1"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Address Line 1"
                                    type="text"
                                    placeholder="Enter address line 1"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="address_line2"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Address Line 2"
                                    type="text"
                                    placeholder="Enter address line 2 (optional)"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-6">
                        <Controller
                            control={control}
                            name="city"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="City"
                                    type="text"
                                    placeholder="Enter city"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-6">
                        <Controller
                            control={control}
                            name="post_code"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Post Code"
                                    type="text"
                                    placeholder="Enter post code"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="country"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Country"
                                    placeholder="Select country"
                                    options={countryOptions}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton
                        onClick={(e) => onCancel?.(e as MouseEvent<HTMLButtonElement>)}
                        color="black"
                        data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
